/* =====================================================================================
   Field overrides — the person who knows more wins.

   See migration 0015 for the rule this supports: an automated refresh never overwrites a
   human entry, and where the two disagree the human value stands while the disagreement is
   raised for that person's attention.

   The client cannot enforce any of that, and does not try. It writes the override and reads
   the effective value back; `atlas.apply_computed()` is what stops the scheduled jobs.
   ===================================================================================== */

import { BACKEND } from './db';
import { dbSiteId } from './ids';
import { explain } from './dberrors';

export interface Override {
  id: string;
  site_id: string;
  field: string;
  value_numeric: number | null;
  value_text: string | null;
  value_organ: string | null;
  rationale: string;
  source: string | null;
  observed_on: string;
  set_at: string;
  set_by_name?: string;
}

export interface Conflict {
  id: string;
  site_id: string;
  site_name: string;
  field: string;
  value_organ: string | null;
  my_value: number;
  computed_numeric: number;
  rationale: string;
  detected_at: string;
}

const live = () => BACKEND === 'supabase';

export async function fetchOverrides(siteId: string): Promise<Override[]> {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase()
    .schema('atlas').from('site_overrides')
    .select('*')
    .eq('site_id', dbSiteId(siteId))
    .is('withdrawn_at', null);
  if (error) throw new Error(explain(error.message, 'correct this figure'));
  const name = await (await import('./supabase')).nameOf();
  return (data ?? []).map((o: any) => ({ ...o, set_by_name: name(o.set_by) }));
}

export async function fetchMyConflicts(): Promise<Conflict[]> {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase()
    .schema('atlas').from('v_my_override_conflicts').select('*');
  if (error) throw new Error(explain(error.message, 'correct this figure'));
  return data ?? [];
}

/**
 * Save a correction. `rationale` is required by a CHECK constraint at ten characters,
 * because an override with no stated reason is indistinguishable from a typo six months
 * later, and it is the reason rather than the number that lets a colleague judge it.
 */
export async function saveOverride(o: {
  siteId: string; field: string; organ?: string | null;
  value: number; rationale: string; source?: string; observedOn?: string;
}) {
  const { supabase, currentMember } = await import('./supabase');
  const m = await currentMember();
  if (!m?.id) throw new Error('No member record for the signed-in user.');
  const { data: org } = await supabase().schema('app').from('orgs').select('id').limit(1).maybeSingle();

  const { error } = await supabase().schema('atlas').from('site_overrides').upsert({
    org_id: org?.id,
    site_id: dbSiteId(o.siteId),
    field: o.field,
    value_organ: o.organ ?? null,
    value_numeric: o.value,
    rationale: o.rationale,
    source: o.source || null,
    observed_on: o.observedOn || new Date().toISOString().slice(0, 10),
    set_by: m.id,
  }, { onConflict: 'site_id,field,value_organ' });

  if (error) {
    if (error.code === '23514') {
      throw new Error('Give a reason of at least ten characters. It is what lets a colleague judge the figure later.');
    }
    if (error.code === '42501') {
      throw new Error('Refused by row level security. Your account cannot write to this organisation.');
    }
    throw new Error(explain(error.message, 'correct this figure'));
  }
}

export async function withdrawOverride(id: string) {
  const { supabase, currentMember } = await import('./supabase');
  const m = await currentMember();
  const { error } = await supabase().schema('atlas').from('site_overrides')
    .update({ withdrawn_at: new Date().toISOString(), withdrawn_by: m?.id })
    .eq('id', id);
  if (error) throw new Error(explain(error.message, 'correct this figure'));
}

export async function resolveConflict(
  id: string, resolution: 'kept_override' | 'accepted_computed' | 'revised',
) {
  const { supabase, currentMember } = await import('./supabase');
  const m = await currentMember();
  const { error } = await supabase().schema('atlas').from('override_conflicts')
    .update({ resolved_at: new Date().toISOString(), resolution, resolved_by: m?.id })
    .eq('id', id);
  if (error) throw new Error(explain(error.message, 'correct this figure'));
}
